import { isArray, isObject } from 'lodash';
import { GROUP } from 'constants/MeshbotConstant';
import { isSearchedTriggerBlock } from './isSearchedTriggerBlock';

/**
 * Recursively traverses a tree of trigger blocks and updates the block that matches the given group and trigger IDs.
 *
 * This function is used to immutably update a specific block inside a nested structure of `when` blocks in a MeshBot rule.
 * It uses `isSearchedTriggerBlock` to determine which block should be updated using the provided `createOrUpdateBlockFn`.
 *
 * @param {Array<Object>} blocks - The current list of blocks to traverse.
 * @param {string | undefined} [selectedGroupId] - The ID of the group that contains the target trigger (optional).
 * @param {string} selectedTriggerId - The ID of the trigger block to update.
 * @param {(block: Object | undefined) => Object} createOrUpdateBlockFn - Function that builds or updates the block.
 * @param {string | undefined} [currentGroupId] - The current group ID being traversed, used to match against `selectedGroupId` (optional).
 * @returns {Array<Object>} The updated blocks array with the modified trigger, if found.
 *
 * @example
 * updateTriggerBlockTree(blocks, 'group-1', 'trigger-5', buildBlockFn);
 */
export const updateTriggerBlockTree = (
    blocks,
    selectedGroupId,
    selectedTriggerId,
    createOrUpdateBlockFn,
    currentGroupId,
) => {
    if (!isArray(blocks)) {
        return blocks;
    }

    return blocks.map((block) => {
        if (!isObject(block)) {
            return block;
        }

        if (block.type === GROUP) {
            return {
                ...block,
                blocks: updateTriggerBlockTree(
                    block.blocks || [],
                    selectedGroupId,
                    selectedTriggerId,
                    createOrUpdateBlockFn,
                    block.id,
                ),
            };
        }

        if (isSearchedTriggerBlock(selectedGroupId, selectedTriggerId, currentGroupId, block.id)) {
            return {
                ...block,
                blocks: [createOrUpdateBlockFn(block.blocks[0])],
            };
        }

        return block;
    });
};
